/**
 * v1 trait scoring, ported.
 *
 * v1 shipped two incompatible scales for the same numbers: the client
 * normalised traits to 0–30 (`calculateTraitScores`) while the
 * `score-assessment` edge function averaged to 0–100. We standardise on
 * **0–100** here and keep the 0–30 conversion available so old rows can still
 * be read.
 *
 * The formulas themselves are v1's:
 *   - 5-point Likert, 1..5, reverse-keyed items flipped (5 - index)
 *   - trait score = sum / (answered × 5)
 *   - archetype = weighted average across the trait→archetype table
 */

import {
  ALL_TRAITS,
  scoreLikert,
  weekitQuestions,
  type WeKitQuestion,
} from "./traitBank";
import {
  TRAIT_TO_ARCHETYPE,
  V1_ARCHETYPES,
  toV2Archetypes,
  type V1Archetype,
} from "./archetypeMap";
import type { ArchetypeVector } from "../archetypes";

/** questionId → chosen option index (0..4). */
export type TraitAnswers = Record<number, number>;

/** v1's legacy 0–30 scale, kept only for reading historical rows. */
export const LEGACY_TRAIT_SCALE_MAX = 30;

/** Convert a legacy 0–30 trait score to the canonical 0–100 scale. */
export function legacyTraitTo100(value: number): number {
  return Math.round((value / LEGACY_TRAIT_SCALE_MAX) * 100);
}

/**
 * Trait scores on the canonical 0–100 scale. Only answered items count, so a
 * partially completed instrument still produces usable, honest scores.
 * Unanswered traits are omitted rather than scored 0 — absence of evidence is
 * not evidence of a low trait.
 */
export function calculateTraitScores100(
  answers: TraitAnswers,
  items: WeKitQuestion[] = weekitQuestions,
): Record<string, number> {
  const totals: Record<string, number> = {};
  const counts: Record<string, number> = {};

  for (const q of items) {
    const idx = answers[q.id];
    if (idx === undefined || idx === null) continue;
    const score = scoreLikert(idx, q.reverseScored);
    totals[q.trait] = (totals[q.trait] ?? 0) + score;
    counts[q.trait] = (counts[q.trait] ?? 0) + 1;
  }

  const out: Record<string, number> = {};
  for (const trait of ALL_TRAITS) {
    const n = counts[trait] ?? 0;
    if (n === 0) continue;
    out[trait] = Math.round(((totals[trait] ?? 0) / (n * 5)) * 100);
  }
  return out;
}

/** How much of the instrument was answered, 0..1. */
export function traitCoverage(
  answers: TraitAnswers,
  items: WeKitQuestion[] = weekitQuestions,
): number {
  if (items.length === 0) return 0;
  const answered = items.filter((q) => answers[q.id] !== undefined).length;
  return answered / items.length;
}

/**
 * Weighted-average trait scores into the eight v1 archetypes, 0–100.
 * Verbatim port of v1's `traitScoresToArchetypes`, minus the scale sniffing —
 * inputs here are always 0–100.
 */
export function traitScoresToV1Archetypes(
  traitScores: Record<string, number>,
): Record<V1Archetype, number> {
  const sums: Record<string, number> = {};
  const weights: Record<string, number> = {};

  for (const [trait, score] of Object.entries(traitScores)) {
    const mappings = TRAIT_TO_ARCHETYPE[trait];
    if (!mappings) continue;
    for (const { archetype, weight } of mappings) {
      sums[archetype] = (sums[archetype] ?? 0) + score * weight;
      weights[archetype] = (weights[archetype] ?? 0) + weight;
    }
  }

  const out = {} as Record<V1Archetype, number>;
  for (const a of V1_ARCHETYPES) {
    const w = weights[a] ?? 0;
    out[a] = w === 0 ? 0 : Math.max(0, Math.min(100, Math.round((sums[a] ?? 0) / w)));
  }
  return out;
}

/** Trait answers → v2 canonical archetype vector, 0–100 per archetype. */
export function traitAnswersToV2Vector(
  answers: TraitAnswers,
  items: WeKitQuestion[] = weekitQuestions,
): ArchetypeVector {
  return toV2Archetypes(traitScoresToV1Archetypes(calculateTraitScores100(answers, items)));
}

/** The learner's strongest traits, highest first. Ties break by trait name. */
export function topTraits(
  traitScores: Record<string, number>,
  limit = 5,
): Array<{ trait: string; score: number }> {
  return Object.entries(traitScores)
    .map(([trait, score]) => ({ trait, score }))
    .sort((a, b) => (b.score - a.score) || (a.trait < b.trait ? -1 : a.trait > b.trait ? 1 : 0))
    .slice(0, limit);
}
